import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';

export type RACIValue = "Responsible" | "Accountable" | "Consulted" | "Informed";

export interface User {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  role: string;
  department: string;
  raci: RACIValue;
  bm: boolean;
}

// Query keys
export const userKeys = {
  all: ['users'] as const,
  lists: () => [...userKeys.all, 'list'] as const,
};

// Storage key
const STORAGE_KEY = 'veritas_users';

// Initial mock data
export const initialUsers: User[] = [
  { id: "1", firstName: "Anna", lastName: "Lindqvist", email: "anna.lindqvist@company.com", role: "Project Manager", department: "Legal", raci: "Responsible", bm: true },
  { id: "2", firstName: "Erik", lastName: "Johansson", email: "erik.johansson@company.com", role: "Analyst", department: "Finance", raci: "Consulted", bm: false },
  { id: "3", firstName: "Maria", lastName: "Svensson", email: "maria.svensson@company.com", role: "Director", department: "Operations", raci: "Accountable", bm: true },
  { id: "4", firstName: "Johan", lastName: "Karlsson", email: "johan.karlsson@company.com", role: "Consultant", department: "Technical", raci: "Informed", bm: false },
  { id: "5", firstName: "Lisa", lastName: "Andersson", email: "lisa.andersson@company.com", role: "Associate", department: "Legal", raci: "Responsible", bm: true },
  { id: "6", firstName: "Oscar", lastName: "Nilsson", email: "oscar.nilsson@company.com", role: "Manager", department: "Finance", raci: "Consulted", bm: false },
  { id: "7", firstName: "Emma", lastName: "Bergström", email: "emma.bergstrom@company.com", role: "Analyst", department: "Technical", raci: "Informed", bm: true },
  { id: "8", firstName: "Gustav", lastName: "Eriksson", email: "gustav.eriksson@company.com", role: "Senior Consultant", department: "Operations", raci: "Accountable", bm: false },
];

// Load users from localStorage or return initial data
const loadUsers = (): User[] => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      return JSON.parse(stored);
    }
  } catch (error) {
    console.error('Failed to load users from localStorage:', error);
  }
  return initialUsers;
};

// Save users to localStorage
const saveUsers = (users: User[]) => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(users));
  } catch (error) {
    console.error('Failed to save users to localStorage:', error);
  }
};

/**
 * Fetch users from localStorage (simulating API)
 */
const fetchUsers = async (): Promise<User[]> => {
  // Simulate network delay
  await new Promise((resolve) => setTimeout(resolve, 50));
  return loadUsers();
};

/**
 * Hook to fetch all users
 */
export const useUsersQuery = () => {
  return useQuery({
    queryKey: userKeys.lists(),
    queryFn: fetchUsers,
    gcTime: 10 * 60 * 1000, // Keep in cache for 10 minutes
  });
};

/**
 * Hook to update a user
 */
export const useUpdateUserMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ userId, updates }: { userId: string; updates: Partial<User> }) => {
      const allUsers = loadUsers();
      const updatedUsers = allUsers.map((user) =>
        user.id === userId ? { ...user, ...updates } : user
      );
      saveUsers(updatedUsers);
      return updatedUsers.find((u) => u.id === userId)!;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: userKeys.lists() });
    },
  });
};

/**
 * Hook to add a new user
 */
export const useAddUserMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (user: Omit<User, 'id'>) => {
      const allUsers = loadUsers();
      const newUser: User = {
        ...user,
        id: Date.now().toString(),
      };
      const updatedUsers = [...allUsers, newUser];
      saveUsers(updatedUsers);
      return newUser;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: userKeys.lists() });
    },
  });
};

/**
 * Hook to delete a user
 */
export const useDeleteUserMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (userId: string) => {
      const allUsers = loadUsers();
      const updatedUsers = allUsers.filter((user) => user.id !== userId);
      saveUsers(updatedUsers);
      return userId;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: userKeys.lists() });
    },
  });
};
